import React from 'react';
import moment from 'moment';
import {useSearchParams} from 'react-router-dom';

import {Box, Stack, Typography} from 'views/components';
import backend from 'utils/backend';

import ChatInput from './ChatInput';
import ChatMessageList from './ChatMessageList';
import ChatSessionList from './ChatSessionList';
import {mapSessionMessages} from './chatUtils';
import useAgentChatTurn from './useAgentChatTurn';

const AgentChatScene = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [sessions, setSessions] = React.useState([]);
  const [selectedSessionRn, setSelectedSessionRn] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [error, setError] = React.useState(null);
  const [followKey, setFollowKey] = React.useState(0);
  const inputRef = React.useRef(null);
  const initialLoadDone = React.useRef(false);
  const initialMessageSent = React.useRef(false);

  const isEventRouterDraft = !selectedSessionRn && searchParams.get('target') === 'eventRouterDiscipline';
  const isDisciplineChat = isEventRouterDraft ||
    sessions.find((s) => s.sessionRn === selectedSessionRn)?.agentTarget === 'EVENT_ROUTER_DISCIPLINE';
  const draftCaseRn = searchParams.get('caseRn');
  const initialDraftMessage = searchParams.get('message');

  const followBottom = React.useCallback(() => setFollowKey((key) => key + 1), []);

  const loadSessions = React.useCallback(async () => {
    try {
      const result = await backend.agentChat.listSessions();
      setSessions(result.sessions || []);
    } catch {
      // The list keeps its current sessions.
    }
  }, []);

  const handleSelectSession = React.useCallback(async (sessionRn) => {
    try {
      const result = await backend.agentChat.getSession(sessionRn);
      followBottom();
      setSelectedSessionRn(sessionRn);
      setMessages(mapSessionMessages(result.messages || []));
      setError(null);
      setSearchParams({sessionRn}, {replace: true});
    } catch {
      setError('Failed to load session.');
    }
  }, [followBottom, setSearchParams]);

  const handleNewChat = React.useCallback((target) => {
    followBottom();
    setSelectedSessionRn(null);
    setMessages([]);
    setError(null);
    setInput('');
    setSearchParams(target ? {target} : {}, {replace: true});
    inputRef.current?.focus();
  }, [followBottom, setSearchParams]);

  const {loading, streamingText, liveUsage, canStop, stopping, send, stop} = useAgentChatTurn({
    onInit: (data) => {
      if (!selectedSessionRn) {
        setSelectedSessionRn(data.session.sessionRn);
        setSessions((prev) => [data.session, ...prev]);
        setSearchParams({sessionRn: data.session.sessionRn}, {replace: true});
      }
    },
    onDone: (message) => {
      setMessages((prev) => [...prev, message]);
      loadSessions();
    },
    onError: setError,
    onClose: () => inputRef.current?.focus(),
    // No done frame came after the stop, so show what the engine saved.
    onStopFallback: handleSelectSession,
  });

  const handleStop = React.useCallback(() => {
    setError(null);
    stop();
  }, [stop]);

  const sendMessage = React.useCallback((messageOverride) => {
    const message = (typeof messageOverride === 'string' ? messageOverride : input).trim();
    if (!message || loading) return;

    setInput('');
    setError(null);
    followBottom();
    setMessages((prev) => [...prev, {role: 'user', text: message, createdAt: moment().toISOString()}]);
    send(message, selectedSessionRn, isEventRouterDraft ? {
      agentTarget: 'EVENT_ROUTER_DISCIPLINE',
      caseRn: draftCaseRn || undefined,
    } : {});
  }, [draftCaseRn, followBottom, input, isEventRouterDraft, loading, selectedSessionRn, send]);

  React.useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    const paramSessionRn = searchParams.get('sessionRn');
    loadSessions().then(() => {
      if (paramSessionRn) {
        handleSelectSession(paramSessionRn);
      }
    });
  }, []);

  React.useEffect(() => {
    if (initialMessageSent.current || !isEventRouterDraft || !initialDraftMessage?.trim()) {
      return;
    }

    initialMessageSent.current = true;
    sendMessage(initialDraftMessage);
  }, [initialDraftMessage, isEventRouterDraft, sendMessage]);

  // The live answer sits where its finished message will go, so React keeps its rendered segments when it completes.
  const displayedMessages = React.useMemo(() => (
    streamingText ? [...messages, {role: 'assistant', text: streamingText}] : messages
  ), [messages, streamingText]);

  return (
    <Stack
      direction={{xs: 'column', md: 'row'}}
      alignItems='stretch'
      sx={{width: '100%', height: {md: 'calc(99vh - 32px)'}}}
    >
      <ChatSessionList
        sessions={sessions}
        selectedSessionRn={selectedSessionRn}
        onSelect={handleSelectSession}
        onNewChat={handleNewChat}
        onRefresh={loadSessions}
      />

      <Box sx={{flex: 1, display: 'flex', backgroundColor: '#FFFFFF'}}>
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
          <ChatMessageList
            messages={displayedMessages}
            loading={loading}
            streaming={Boolean(streamingText)}
            liveUsage={liveUsage}
            stopping={stopping}
            emptyText={selectedSessionRn ?
              'No messages in this session.' :
              `Start a conversation with ${isEventRouterDraft ? 'Hudson' : 'Chanel'}`}
            followKey={followKey}
          />

          {error && (
            <Box sx={{px: '40px', py: '8px', backgroundColor: '#FEF2F2', borderTop: '1px solid #FECACA'}}>
              <Typography sx={{fontSize: '12px', color: '#EF4444', maxWidth: '720px', mx: 'auto'}}>
                {error}
              </Typography>
            </Box>
          )}

          <ChatInput
            inputRef={inputRef}
            value={input}
            onChange={setInput}
            onSend={sendMessage}
            loading={loading}
            stoppable={!isDisciplineChat}
            canStop={canStop}
            onStop={handleStop}
          />
        </Box>
      </Box>
    </Stack>
  );
};

export default AgentChatScene;
